// ذخیره‌گاه محلی حالت آفلاین (ARD-offline §۲): IndexedDB در مرورگر و پیاده‌سازی حافظه‌ای برای Vitest (fake-indexeddb در Workspace نیست —
// C-AGENT-1549؛ مسیر IndexedDB واقعی را Playwright می‌آزماید). سه Store: meta (شناسه دستگاه و شمارنده شماره موقت — بدون داده مستأجر)،
// vault (کلید غیرقابل استخراج و Snapshot رمزشده) و outbox (عملیات رمزشده). هیچ توکنی در این ذخیره‌گاه نوشته نمی‌شود.

export type StoreName = 'meta' | 'vault' | 'outbox';

export interface OfflineStore {
  get<T>(store: StoreName, key: string): Promise<T | undefined>;
  put(store: StoreName, key: string, value: unknown): Promise<void>;
  delete(store: StoreName, key: string): Promise<void>;
  /** همه ردیف‌ها به ترتیب کلید. */
  entries<T>(store: StoreName): Promise<{ key: string; value: T }[]>;
  /** پاک کردن کامل یک Store. */
  clear(store: StoreName): Promise<void>;
}

export const OFFLINE_DB_NAME = 'phx-offline-v1';
const STORES: readonly StoreName[] = ['meta', 'vault', 'outbox'];

function request<T>(value: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    value.onsuccess = () => resolve(value.result);
    value.onerror = () => reject(value.error ?? new Error('indexeddb_error'));
  });
}

/** IndexedDB مرورگر. */
export function createIndexedDbStore(name = OFFLINE_DB_NAME): OfflineStore {
  let opening: Promise<IDBDatabase> | null = null;
  const open = () => {
    opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => {
        for (const store of STORES) {
          if (!req.result.objectStoreNames.contains(store)) {
            req.result.createObjectStore(store);
          }
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        opening = null;
        reject(req.error ?? new Error('indexeddb_open_failed'));
      };
    });
    return opening;
  };
  const run = async <T>(store: StoreName, mode: IDBTransactionMode, work: (os: IDBObjectStore) => IDBRequest<T>) => {
    const db = await open();
    return request(work(db.transaction(store, mode).objectStore(store)));
  };
  return {
    get: <T>(store: StoreName, key: string) => run(store, 'readonly', (os) => os.get(key) as IDBRequest<T | undefined>),
    put: async (store, key, value) => {
      await run(store, 'readwrite', (os) => os.put(value, key));
    },
    delete: async (store, key) => {
      await run(store, 'readwrite', (os) => os.delete(key));
    },
    entries: async <T>(store: StoreName) => {
      const keys = (await run(store, 'readonly', (os) => os.getAllKeys())) as IDBValidKey[];
      const values = (await run(store, 'readonly', (os) => os.getAll())) as T[];
      return keys.map((key, index) => ({ key: String(key), value: values[index] as T }));
    },
    clear: async (store) => {
      await run(store, 'readwrite', (os) => os.clear());
    },
  };
}

/** ذخیره‌گاه حافظه‌ای (تست و مرورگر بدون IndexedDB). */
export function createMemoryStore(): OfflineStore & { raw: Map<string, unknown> } {
  const raw = new Map<string, unknown>();
  const id = (store: StoreName, key: string) => `${store}\u0000${key}`;
  return {
    raw,
    get: async <T>(store: StoreName, key: string) => raw.get(id(store, key)) as T | undefined,
    put: async (store, key, value) => {
      raw.set(id(store, key), value);
    },
    delete: async (store, key) => {
      raw.delete(id(store, key));
    },
    entries: async <T>(store: StoreName) =>
      [...raw.entries()]
        .filter(([key]) => key.startsWith(`${store}\u0000`))
        .map(([key, value]) => ({ key: key.slice(store.length + 1), value: value as T }))
        .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)),
    clear: async (store) => {
      for (const key of [...raw.keys()]) {
        if (key.startsWith(`${store}\u0000`)) {
          raw.delete(key);
        }
      }
    },
  };
}

/** ذخیره‌گاه پیش‌فرض مرورگر. */
export function defaultOfflineStore(): OfflineStore {
  return typeof indexedDB === 'undefined' ? createMemoryStore() : createIndexedDbStore();
}

/** کارخانه ذخیره‌گاه Provider (تست ذخیره‌گاه حافظه‌ای قابل بازرسی جایگزین می‌کند). */
export const offlineStoreFactory: { create: () => OfflineStore } = { create: defaultOfflineStore };
